// NOTE: I use the library `chroma-js` in this repo for color stuff, and I should use that dependency rather than these helpers. Keeping this because I'm lazy.

import { clamp } from '@/utils';

type Color = [number, number, number, number];

export function tweak(
  color: Color,
  callbacks: {
    hue?: (hue: number) => number;
    sat?: (sat: number) => number;
    lit?: (lit: number) => number;
    alpha?: (alpha: number) => number;
  }
) {
  const currentAlpha = color[3] || 1;

  let h = callbacks.hue ? callbacks.hue(color[0]) : color[0];
  let s = callbacks.sat ? callbacks.sat(color[1]) : color[1];
  let l = callbacks.lit ? callbacks.lit(color[2]) : color[2];
  let a = callbacks.alpha
    ? callbacks.alpha(currentAlpha)
    : currentAlpha;

  h = clamp(h, 0, 360);
  s = clamp(s, 0, 100);
  l = clamp(l, 0, 100);
  a = clamp(a, 0, 1);

  return `hsl(${h}deg ${s}% ${l}% / ${a})`;
}

export function darken(color: Color, amount: number) {
  // `amount` is a number from 0 to 1.
  // eg. 0.1 means "darken by 10%"
  // I need to multiply the current lightness by 0.9
  const tweaks = {
    lit: (val: number) => val * (1 - amount),
  };
  return tweak(color, tweaks);
}

export function lighten(color: Color, amount: number) {
  // `amount` is a number from 0 to 1.
  // eg. 0.1 means "darken by 10%"
  // I need to multiply the current lightness by 0.9
  const tweaks = {
    lit: (val: number) => val * (amount + 1),
  };
  return tweak(color, tweaks);
}

export function convertHSLToRGB(h: number, s: number, l: number) {
  // Convert saturation and lightness from percentages to decimals
  s = s / 100;
  l = l / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r, g, b;

  if (h >= 0 && h < 60) {
    [r, g, b] = [c, x, 0];
  } else if (h >= 60 && h < 120) {
    [r, g, b] = [x, c, 0];
  } else if (h >= 120 && h < 180) {
    [r, g, b] = [0, c, x];
  } else if (h >= 180 && h < 240) {
    [r, g, b] = [0, x, c];
  } else if (h >= 240 && h < 300) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }

  // Convert to 0-255 range and round
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

export function convertHSLToLCH(
  hue: number,
  sat: number,
  lit: number
) {
  // First convert HSL to RGB
  const [r, g, blue] = convertHSLToRGB(hue, sat, lit);

  // Convert RGB to XYZ
  // First normalize RGB to 0-1 range
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = blue / 255;

  // Convert to linear RGB
  const rLinear =
    r1 <= 0.04045 ? r1 / 12.92 : Math.pow((r1 + 0.055) / 1.055, 2.4);
  const gLinear =
    g1 <= 0.04045 ? g1 / 12.92 : Math.pow((g1 + 0.055) / 1.055, 2.4);
  const bLinear =
    b1 <= 0.04045 ? b1 / 12.92 : Math.pow((b1 + 0.055) / 1.055, 2.4);

  // Convert to XYZ using sRGB matrix
  const x =
    rLinear * 0.4124564 + gLinear * 0.3575761 + bLinear * 0.1804375;
  const y =
    rLinear * 0.2126729 + gLinear * 0.7151522 + bLinear * 0.072175;
  const z =
    rLinear * 0.0193339 + gLinear * 0.119192 + bLinear * 0.9503041;

  // Convert XYZ to Lab
  const xn = 0.95047;
  const yn = 1.0;
  const zn = 1.08883;

  const x1 = x / xn;
  const y1 = y / yn;
  const z1 = z / zn;

  const fx =
    x1 > 0.008856 ? Math.pow(x1, 1 / 3) : 7.787 * x1 + 16 / 116;
  const fy =
    y1 > 0.008856 ? Math.pow(y1, 1 / 3) : 7.787 * y1 + 16 / 116;
  const fz =
    z1 > 0.008856 ? Math.pow(z1, 1 / 3) : 7.787 * z1 + 16 / 116;

  const l = 116 * fy - 16;
  const a = 500 * (fx - fy);
  const b = 200 * (fy - fz);

  // Convert Lab to LCH
  const c = Math.sqrt(a * a + b * b);
  const h = Math.atan2(b, a) * (180 / Math.PI);
  const h1 = h < 0 ? h + 360 : h;

  return [l, c, h1];
}
